import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Chip,
  FormControlLabel,
  Checkbox,
  Grid,
  Alert,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Snackbar,
} from '@mui/material';
import QuizIcon from '@mui/icons-material/Quiz';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { StageProcessor } from '../components/StageProcessor';
import { getRequirementQuestions } from '../api/stageFunctions';
import { Question } from '../types/pipeline';
import { useStageCache } from '../hooks/useStageCache';
import { STAGE_NUMBERS } from '../utils/cacheUtils';
import { getButtonText } from '../utils/cacheUIHelpers';

interface GenerateQuestionsProps {
  onComplete: (questions: Question[], answers: Record<string, string>, commodityDescription: string) => void;
  initialDescription?: string;
  flowId?: string | null;
  initialAnswers?: Record<string, string>;
}

const GenerateQuestions: React.FC<GenerateQuestionsProps> = ({
  onComplete,
  initialDescription = '',
  flowId,
  initialAnswers = {},
}) => {
  const [commodityDescription, setCommodityDescription] = useState(initialDescription);
  console.log('[GenerateQuestions] initialDescription prop:', initialDescription);
  console.log('[GenerateQuestions] state.commodityDescription init:', commodityDescription);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>(initialAnswers);
  const [triggerGeneration, setTriggerGeneration] = useState(0);
  const [useCachedData, setUseCachedData] = useState(true);
  const [questionsModified, setQuestionsModified] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [copyToastOpen, setCopyToastOpen] = useState(false);

  const sortedQuestions = useMemo(() => {
    const isRequired = (q: Question) =>
      q.priority === 'High' || q.priority === 1 || q.priority === 2;

    return [...questions].sort((a, b) => {
      const aReq = isRequired(a) ? 1 : 0;
      const bReq = isRequired(b) ? 1 : 0;
      if (aReq !== bReq) {
        return bReq - aReq; // required first
      }
      return 0;
    });
  }, [questions]);

  // Use cache hook for Stage 1 question generation
  const { 
    cachedData, 
    isCacheValid, 
    isCheckingCache,
    refreshCache 
  } = useStageCache({
    flowId,
    stageNumber: STAGE_NUMBERS.QUESTIONS, // Stage 1 for question generation
    inputData: {
      commodity_description: commodityDescription,
      questions_and_answers: [] // Empty for just question generation
    },
    enabled: useCachedData && flowId !== null,
  });

  // Default checkbox to match cache availability (disabled when no cache)
  React.useEffect(() => {
    if (!isCheckingCache) {
      setUseCachedData(isCacheValid);
    }
  }, [isCheckingCache, isCacheValid]);

  // Load cached questions and answers if available and use cache is enabled
  React.useEffect(() => {
    if (cachedData && useCachedData && cachedData.questions && cachedData.questions.length > 0) {
      console.log('Loading cached questions:', cachedData.questions);
      setQuestions(cachedData.questions);
      
      // Load cached answers if available
      if (cachedData.answers) {
        console.log('Loading cached answers:', cachedData.answers);
        setAnswers(cachedData.answers);
      }
    }
  }, [cachedData, useCachedData]);

  const handleGenerateQuestions = useCallback(() => {
    if (commodityDescription.length < 10) {
      alert('Please enter a more detailed commodity description');
      return;
    }
    
    // Clear existing questions
    setQuestions([]);
    
    // If not using cache, refresh it first
    if (!useCachedData) {
      refreshCache();
    }
    
    // Trigger generation
    setTriggerGeneration(prev => prev + 1);
  }, [commodityDescription, useCachedData, refreshCache]);

  const handleQuestionsReceived = useCallback((questionsData: Question[]) => {
    console.log('Questions received:', questionsData);
    setQuestions(questionsData);
    // Clear any old answers when new questions are generated
    setAnswers({});
    // Reset the modified flag since we have fresh questions
    setQuestionsModified(false);
  }, []);
  
  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleUseCacheChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const useCache = event.target.checked;
    setUseCachedData(useCache);
    
    // If switching to not use cache, clear current questions
    if (!useCache) {
      setQuestions([]);
    }
  };

  // Question editing functions
  const handleStartEditQuestion = (question: Question) => {
    setEditingQuestionId(question.id);
    setEditingQuestion({ ...question });
  };

  const handleSaveEditQuestion = () => {
    if (editingQuestion && editingQuestionId) {
      setQuestions(prevQuestions => 
        prevQuestions.map(q => 
          q.id === editingQuestionId ? editingQuestion : q
        )
      );
      setQuestionsModified(true);
      setEditingQuestionId(null);
      setEditingQuestion(null);
    }
  };

  const handleCancelEditQuestion = () => {
    setEditingQuestionId(null);
    setEditingQuestion(null);
  };

  const handleDeleteQuestion = (questionId: string) => {
    setQuestions(prevQuestions => prevQuestions.filter(q => q.id !== questionId));
    // Also remove the answer for this question
    setAnswers(prev => {
      const newAnswers = { ...prev };
      delete newAnswers[questionId];
      return newAnswers;
    });
    setQuestionsModified(true);
  };

  const handleAddQuestion = () => {
    const newQuestion: Question = {
      id: `custom-${Date.now()}`,
      category: 'custom',
      question: 'New custom question',
      priority: 'Medium',
      helpText: 'Enter details for your custom requirement'
    };
    setQuestions(prevQuestions => [...prevQuestions, newQuestion]);
    setQuestionsModified(true);
    // Start editing the new question immediately
    handleStartEditQuestion(newQuestion);
  };

  const handleEditQuestionField = (field: keyof Question, value: string | number) => {
    if (editingQuestion) {
      setEditingQuestion({
        ...editingQuestion,
        [field]: value
      });
    }
  };

  // Helper to check if we have cached data
  const hasCachedData = cachedData && cachedData.questions && cachedData.questions.length > 0;

  const formatQuestionsForCopy = () => {
    if (!questions || questions.length === 0) return '';
    const lines: string[] = [];
    lines.push('Clarifying Questions');
    lines.push('');
    questions.forEach((q, idx) => {
      const cat = q.category ? ` [${String(q.category).toUpperCase()}]` : '';
      lines.push(`${idx + 1}. ${q.question}${cat}`);
    });
    lines.push('');
    lines.push('Please reply with brief answers; we will translate them into Salient Characteristics.');
    return lines.join('\n');
  };

  const handleCopyAll = async () => {
    try {
      const text = formatQuestionsForCopy();
      if (!text) return;
      await navigator.clipboard.writeText(text);
      setCopyToastOpen(true);
    } catch (e) {
      alert('Could not copy to clipboard.');
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          <QuizIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
          Stage 1: Generate Questions
        </Typography>
        
        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
          Provide a detailed commodity description to generate relevant clarifying questions.
        </Typography>

        {/* Commodity Description Input */}
        <TextField
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          label="Commodity Description"
          value={commodityDescription}
          onChange={(e) => setCommodityDescription(e.target.value)}
          placeholder="Example: X-band radar system for maritime surveillance with long-range detection capabilities"
          sx={{ mb: 3 }}
        />

        {/* Cache Control */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={useCachedData}
                onChange={handleUseCacheChange}
                color="primary"
                disabled={isCheckingCache || !isCacheValid}
              />
            }
            label="Use cached data when available"
          />
        </Box>

        {/* Generate Button */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<QuizIcon />}
            onClick={handleGenerateQuestions}
            disabled={commodityDescription.length < 10 || isCheckingCache}
          >
            {getButtonText('Generate Questions', useCachedData, hasCachedData)}
          </Button>
        </Box>

        {/* Generated Questions Display */}
        {questions.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Generated Questions ({questions.length})
                {questionsModified && (
                  <Chip 
                    label="Modified" 
                    size="small" 
                    color="warning" 
                    sx={{ ml: 2 }}
                  />
                )}
              </Typography>
              <Box>
                <Button
                  variant="text"
                  onClick={handleCopyAll}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  Copy All
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddQuestion}
                  size="small"
                >
                  Add Question
                </Button>
              </Box>
            </Box>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              Answer the questions below to help generate more accurate requirements. High priority questions are required.
              {questionsModified && ' (Questions have been modified - will trigger fresh generation)'}
            </Alert>
            
            <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
              <Grid container spacing={2}>
                {sortedQuestions.map((q, index) => {
                  const isEditing = editingQuestionId === q.id;
                  const displayQuestion = isEditing ? editingQuestion : q;
                  
                  return (
                    <Grid item xs={12} key={q.id}>
                      <Box sx={{ mb: 2, pb: 2, borderBottom: index < sortedQuestions.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {isEditing ? (
                              <>
                                <FormControl size="small" sx={{ minWidth: 120, mr: 1 }}>
                                  <Select
                                    value={displayQuestion?.category || 'custom'}
                                    onChange={(e) => handleEditQuestionField('category', e.target.value)}
                                    size="small"
                                  >
                                    <MenuItem value="functional">Functional</MenuItem>
                                    <MenuItem value="performance">Performance</MenuItem>
                                    <MenuItem value="compatibility">Compatibility</MenuItem>
                                    <MenuItem value="features">Features</MenuItem>
                                    <MenuItem value="custom">Custom</MenuItem>
                                  </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                  <Select
                                    value={displayQuestion?.priority || 'Medium'}
                                    onChange={(e) => handleEditQuestionField('priority', e.target.value)}
                                    size="small"
                                  >
                                    <MenuItem value="High">High</MenuItem>
                                    <MenuItem value="Medium">Medium</MenuItem>
                                    <MenuItem value="Low">Low</MenuItem>
                                  </Select>
                                </FormControl>
                              </>
                            ) : (
                              <>
                                <Chip 
                                  label={q.category} 
                                  size="small" 
                                  color="primary" 
                                  sx={{ mr: 1 }} 
                                />
                                <Chip 
                                  label={q.priority} 
                                  size="small" 
                                  color={
                                    q.priority === 'High' || q.priority === 1 || q.priority === 2
                                      ? 'error' 
                                      : q.priority === 'Medium' 
                                      ? 'warning' 
                                      : 'success'
                                  }
                                />
                                {(q.priority === 'High' || q.priority === 1 || q.priority === 2) && (
                                  <Typography variant="body2" color="error" sx={{ ml: 1, fontWeight: 'medium' }}>
                                    Required
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>
                          <Box>
                            {isEditing ? (
                              <>
                                <IconButton 
                                  onClick={handleSaveEditQuestion} 
                                  color="primary"
                                  size="small"
                                >
                                  <SaveIcon />
                                </IconButton>
                                <IconButton 
                                  onClick={handleCancelEditQuestion}
                                  size="small"
                                >
                                  <CancelIcon />
                                </IconButton>
                              </>
                            ) : (
                              <>
                                <IconButton 
                                  onClick={() => handleStartEditQuestion(q)}
                                  size="small"
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton 
                                  onClick={() => handleDeleteQuestion(q.id)}
                                  color="error"
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </Box>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            value={displayQuestion?.question || ''}
                            onChange={(e) => handleEditQuestionField('question', e.target.value)}
                            variant="outlined"
                            sx={{ mb: 1 }}
                          />
                        ) : (
                          <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                            {q.question}
                          </Typography>
                        )}
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          variant="outlined"
                          value={answers[q.id] || ''}
                          onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                          placeholder={displayQuestion?.helpText || 'Enter your answer...'}
                          required={displayQuestion?.priority === 'High' || displayQuestion?.priority === 1 || displayQuestion?.priority === 2}
                          disabled={isEditing}
                        />
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
            
            {/* Continue Button */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  console.log('[GenerateQuestions] Continue clicked. Passing commodity to next stage:', commodityDescription);
                  // Check if high priority questions are answered
                  const highPriorityQuestions = questions.filter(q => 
                    q.priority === 'High' || q.priority === 1 || q.priority === 2
                  );
                  const unansweredHighPriority = highPriorityQuestions.filter(q => !answers[q.id]?.trim());
                  
                  if (unansweredHighPriority.length > 0) {
                    alert(`Please answer all high priority questions (${unansweredHighPriority.length} remaining)`);
                    return;
                  }
                  
                  onComplete(questions, answers, commodityDescription);
                }}
                disabled={questions.length === 0}
              >
                Continue to Salient Characteristics
              </Button>
            </Box>
          </Box>
        )}

        <Snackbar
          open={copyToastOpen}
          autoHideDuration={2000}
          onClose={() => setCopyToastOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setCopyToastOpen(false)} severity="success" sx={{ width: '100%' }}>
            Questions copied to clipboard
          </Alert>
        </Snackbar>

        {/* Stage Processor for API calls */}
        {triggerGeneration > 0 && (
          <StageProcessor
            title=""
            processFunction={() => getRequirementQuestions(commodityDescription, flowId, questionsModified)}
            onSuccess={handleQuestionsReceived}
            trigger={triggerGeneration}
            showStatus={true}
          >
            {() => null}
          </StageProcessor>
        )}
      </Paper>
    </Box>
  );
};

export default GenerateQuestions;
